import { convert } from './color-convert';
import { getDeltaE00, type Lab } from './delta-e';

const COLORNAME_DEFAULT_VARIANCE = 20;

/**
 * Computes the HSV color from selecting a point in the saturation gradient.
 * Adapted from solid-color's calculateChange() function.
 * @param e Mouse Event
 * @param container Saturation Div
 * @returns A Saturation and Value color
 */
export const calculateChange = (
  e: MouseEvent | TouchEvent,
  container: HTMLDivElement
): { s: number; v: number } => {
  const { width: containerWidth, height: containerHeight } =
    container.getBoundingClientRect();
  const x: number = e instanceof MouseEvent ? e.pageX : e.touches[0].pageX;
  const y: number = e instanceof MouseEvent ? e.pageY : e.touches[0].pageY;
  let left = x - (container.getBoundingClientRect().left + window.pageXOffset);
  let top = y - (container.getBoundingClientRect().top + window.pageYOffset);

  if (left < 0) {
    left = 0;
  } else if (left > containerWidth) {
    left = containerWidth;
  }

  if (top < 0) {
    top = 0;
  } else if (top > containerHeight) {
    top = containerHeight;
  }

  const saturation = left / containerWidth;
  const bright = 1 - top / containerHeight;

  return {
    s: saturation,
    v: bright,
  };
};

const namedColors: Record<string, string> = {
  // Basic colors
  aqua: '#00FFFF',
  red: '#FF0000',
  green: '#008000',
  blue: '#0000FF',
  yellow: '#FFFF00',
  pink: '#FFC0CB',
  purple: '#800080',
  orange: '#FFA500',
  brown: '#A52A2A',
  gray: '#808080',
  black: '#000000',
  white: '#FFFFFF',
  cyan: '#00FFFF',
  magenta: '#FF00FF',
  lime: '#00FF00',

  // Light variants
  lightRed: '#FFB3B3',
  lightGreen: '#90EE90',
  lightBlue: '#ADD8E6',
  lightYellow: '#FFFFE0',
  lightPink: '#FFB6C1',
  lightPurple: '#DDA0DD',
  lightOrange: '#FFE4B5',
  lightBrown: '#DEB887',
  lightGray: '#D3D3D3',
  lightCyan: '#E0FFFF',
  lightMagenta: '#FFB6FF',
  lightLime: '#CCFFCC',

  // Dark variants
  darkRed: '#8B0000',
  darkGreen: '#006400',
  darkBlue: '#00008B',
  darkYellow: '#B8860B',
  darkPink: '#FF1493',
  darkPurple: '#4B0082',
  darkOrange: '#FF8C00',
  darkBrown: '#654321',
  darkGray: '#404040',
  darkCyan: '#008B8B',
  darkMagenta: '#8B008B',
  darkLime: '#32CD32',

  // Additional common colors
  navy: '#000080',
  maroon: '#800000',
  olive: '#808000',
  teal: '#008080',
  silver: '#C0C0C0',
  gold: '#FFD700',
  indigo: '#4B0082',
  violet: '#EE82EE',
  turquoise: '#40E0D0',
  coral: '#FF7F50',
  salmon: '#FA8072',
  khaki: '#F0E68C',
  plum: '#DDA0DD',
  beige: '#F5F5DC',
  tan: '#D2B48C',
};

/**
 * Convert hex (#RRGGBB) → LAB color space for perceptual comparison
 */
function hexToLab(hex: string): Lab {
  const [r, g, b] = convert.hex.rgb(hex);
  const [L, A, B] = convert.rgb.lab([r, g, b]);
  return { L, A, B };
}

/**
 * Finds the closest named color using CIEDE2000 perceptual distance
 * @param hexColor - Input hex color (#rrggbb)
 * @param variance - Maximum ΔE (perceptual distance) allowed. Default 20
 */
export function getClosestNamedColor(
  hexColor: string,
  variance = COLORNAME_DEFAULT_VARIANCE
): string {
  if (hexColor === '*varies*') {
    return hexColor;
  }
  const targetLab = hexToLab(hexColor);
  let closestName = 'unknown';
  let minDelta = Infinity;

  for (const [name, hex] of Object.entries(namedColors)) {
    const lab = hexToLab(hex);
    try {
      const delta = getDeltaE00(targetLab, lab);
      if (typeof delta === 'number' && !isNaN(delta) && delta < minDelta) {
        minDelta = delta;
        closestName = formatColorName(name);
      }
    } catch (error) {
      // Skip this color if delta calculation fails
      console.warn('Failed to calculate delta for color:', name, error);
    }
  }

  // Return only if within perceptual margin
  return minDelta <= variance ? closestName : 'Custom';
}

/**
 * Converts camelCase string to readable format with capitalized words (e.g., "lightRed" -> "Light Red")
 */
export function formatColorName(colorName: string): string {
  return colorName
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
